import { useEffect } from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Button } from '@mui/material';
import { ErrorHeader } from '../../shared/errorHeader';
import { useParams } from 'react-router-dom';
import { formatHeight } from '../../utils/dataFormatters';
import { useForm } from 'react-hook-form';
import { useAthlete } from '../../providers/athleteProvider';

export function ManageAthleteForm() {
  const params = useParams();
  const { athlete, loading, updateAthlete, error } = useAthlete();
  const { register, handleSubmit, reset, formState: { isDirty, isSubmitting } } = useForm({
    defaultValues: {
      parentNames: athlete?.pageInfo?.parentNames || '',
      cellPhone: athlete?.pageInfo?.cellPhone || '',
      addressLine1: athlete?.pageInfo?.addressLine1 || '',
    }
  });

  useEffect(() => {
    // Reset form when member data changes
    if (athlete) {
      reset({
        parentNames: athlete.pageInfo?.parentNames || '',
        cellPhone: athlete.pageInfo?.cellPhone || '',
        addressLine1: athlete.pageInfo?.addressLine1 || '',
      });
    }
  }, [athlete]);

  const onSubmit = async (data: any) => {
    if (!athlete) return;
    await updateAthlete({
      ...athlete,
      pageInfo: {
        ...athlete.pageInfo,
        parentNames: data.parentNames,
        cellPhone: data.cellPhone,
        addressLine1: data.addressLine1,
        city: athlete.pageInfo?.city || '',
        state: athlete.pageInfo?.state || '',
        postalCode: athlete.pageInfo?.postalCode || '',
      }
    });
  };

  if (loading) return <CircularProgress />;
  if (!athlete) return <ErrorHeader errorMessage={error || "Member not found"} />;

  const birthDate = athlete.birthDate ? new Date(athlete.birthDate) : undefined;
  const age = birthDate ? new Date().getFullYear() - birthDate.getFullYear() : '';

  return (
    <Box>
      <ErrorHeader errorMessage={error} />
      <Box>
        
        <br />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={athlete.orgSeason?.logoLink} height={50} alt="profile" />
          {athlete.orgSeason && (
            <h2 style={{ margin: '0 10px' }}>{athlete.orgSeason.name}</h2>
          )}
        </div>

        <h1>
          {athlete.firstName} {athlete.lastName}{' '}
        </h1>
        {athlete.profileLink && <img src={athlete.profileLink} height={200} alt="profile" />}
        {athlete.team && (
          <h3 style={{ margin: '0 10px' }}>
            {athlete.team.name} - #{athlete.jerseyNumber} - {athlete.position}
          </h3>
        )}

        <br />

        <h2>Bio</h2>
        <Box sx={{ width: 300 }}>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white', width: 75 }}>Height:</TableCell>
                  <TableCell size='small' sx={{ backgroundColor: 'white', color: 'black' }}>
                    <span>{formatHeight(athlete?.height)}</span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white' }}>Weight:</TableCell>
                  <TableCell size='small'>
                    <span>{athlete?.weight}</span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white' }}>Birth Date:</TableCell>
                  <TableCell size='small'>
                    <span>{birthDate ? birthDate.toLocaleDateString() : ''}</span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white' }}>Age:</TableCell>
                  <TableCell size='small'>
                    <span>{age}</span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <br />
        <h2>Contact Info</h2>
        <Box sx={{ width: 400 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white', width: 75 }}>Parents:</TableCell>
                    <TableCell size='small'>
                      <TextField
                        {...register("parentNames")}
                        id="parentNames"
                        label="Parent Names"
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 25 }}
                        style={{ width: 200 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white' }}>Cell Phone:</TableCell>
                    <TableCell size='small'>
                      <TextField
                        {...register("cellPhone")}
                        id="cellPhone"
                        label="Cell Phone"
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 25 }}
                        style={{ width: 200 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell size='small' sx={{ backgroundColor: '#0062A3', color: 'white', verticalAlign: 'top' }}>Address:</TableCell>
                    <TableCell size='small'>
                      <TextField
                        {...register("addressLine1")}
                        id="address"
                        label="Address"
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 50 }}
                        style={{ width: 200 }}
                      />
                      <span>
                        {athlete?.pageInfo?.addressLine1}<br />
                        {athlete?.pageInfo?.city},{athlete?.pageInfo?.state} {athlete?.pageInfo?.postalCode}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Button type="submit" variant="contained" color="primary" disabled={!isDirty || isSubmitting}>
              Save
            </Button>
          </form>
        </Box>
        
      </Box>
    </Box>
  );
}